import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useShallow } from 'zustand/react/shallow';

// --- STORES ---
import {
  usePrintsUiStore,
  usePrintsConfigStore,
  usePrintsOrderStore,
} from '../stores';

// --- HOOKS ---
import { useAddPhotos } from '../hooks/use-add-photos';
import { useAutoOpenPhotoLoader } from '../hooks/use-auto-open-photo-loader';
import { useAlbumLoader } from '../hooks/use-album-loader';
import { useEffectOnce } from '../hooks/use-effect-once';
import { useBeforeUnloadWarning } from '../hooks/use-before-unload-warning';
import { useGa4EventSubscriber } from '../subscribers/use-ga4-event-subscriber';
import { useFocusStyles } from '../hooks/use-focus-styles';
import { useSizesWithPhotos } from '../hooks/use-sizes-with-photos';
import { useSelectedDeliveryOption } from '../hooks/use-selected-delivery-option';
import { useSelectedFinish } from '../hooks/use-selected-finish';
import { useInvalidDeliveryOptionSelected } from '../hooks/use-invalid-delivery-option-selected';
import { useAvailableSizesForSelectedDeliveryAndFinish } from '../hooks/use-available-sizes-for-selected-delivery-and-finish';
import useViewportResize from '../hooks/use-viewport-resize';
import useCollapseOnScroll from '../hooks/use-collapse-on-scroll';

// --- EVENTS ---
import UploadManager from '../event-handlers/upload-manager';
import LegacyEventHandler from '../event-handlers/legacy-event-handler';

// --- CORE ---
import { VIEW_MODE } from '../constants';
import { PrintsBuilderUrlsShape, FeaturesShape } from '../prop-types';
import startSessionTimeout from '../../_utils/tools/session-timeout';
import { sortedAndFilteredImages } from '../common/images-helper';
import { totalQuantity } from '../common/totalizing-mixin';
import { allPhotosCollectedByRootImage } from '../common/collect-by-root-image';
import SizeHelpers from '../common/size-helpers';

// --- COMPONENTS ---
import SiteLogo from '../../_application/site-logo';
import Totaliser from '../common/totalizer';
import PhotosFilter from './photos-filter';
import UploadProgressBar from '../common/upload-progress-bar';
import FullScreenSpinner from '../../_prism-builder/desktop/notices/full-screen-spinner';
import BulkQuantityChange from '../common/bulk-quantity-change';
import PrintFinishTabs from '../common/print_finish_tabs';
import AddToCartControls from '../common/add-to-cart-controls';
import FinishSelector from './finish-selector';
import DeliverySelector from './delivery-selector';
import Button from '../../_common/button';
import Footer from './footer';
import MainView from './main-view';
import DebugControls from '../common/debug-controls';
import PhotoUndoRemovalButton from '../common/photo-undo-removal-button';

// --- DUPLICATE DETECTION ---
import { DuplicatePhotoViewToggle } from '../common/duplicate-detection/duplicate-photo-view-toggle';
import { listDuplicatePhotos } from '../common/duplicate-detection/list-duplicate-photos';
import { DuplicatesWarningModal } from '../common/duplicate-detection/duplicates-warning-modal';

// --- MODALS ---
import PhotoRemoveConfirmation from '../common/photo-remove-confirmation';
import AdvancedEditorWrapper from '../common/advanced-editor/advanced-editor-wrapper';
import InvalidSizesModal from '../common/invalid-sizes-modal';
import InvalidDeliveryOptionModal from '../common/invalid-delivery-option-modal';
import PhotoRemoveAllButton from '../common/photo-remove-all-button';

const MobilePrintsBuilder = ({
  urls,
  features,
  uploadFromGalleryListenerUrl,
  initSessionTimeout,
}) => {
  const { upload_from_album: uploadFromAlbumUrl } = urls;
  const { printsBuilderDuplicateDetectionDisabled, debugEnabled } = features;
  const duplicatePhotoDetectionEnabled =
    !printsBuilderDuplicateDetectionDisabled;

  // --- STATE ---
  const ready = usePrintsUiStore((state) => state.ready);
  const currentlyLoading = usePrintsUiStore((state) => state.currentlyLoading);
  const currentView = usePrintsUiStore((state) => state.currentView);
  const isSaving = usePrintsUiStore((state) => state.isSaving);
  const viewingSizeKey = usePrintsUiStore((state) => state.viewingSizeKey);

  const allFinishes = usePrintsConfigStore((state) => state.allFinishes);
  const allDeliveryOptions = usePrintsConfigStore(
    (state) => state.allDeliveryOptions,
  );
  const finishesByDeliveryOption = usePrintsConfigStore(
    (state) => state.availableFinishesByDeliveryOption,
  );

  const photos = usePrintsOrderStore((state) => state.photos);
  const hasRemovedPhotos = usePrintsOrderStore((state) => state.removedPhotos);

  // --- ACTIONS ---
  const { handleAddToCart, bulkPhotoResize } = usePrintsOrderStore(
    useShallow((state) => ({
      handleAddToCart: state.handleAddToCart,
      bulkPhotoResize: state.bulkPhotoResize,
    })),
  );

  const { loadInitialData } = usePrintsConfigStore(
    useShallow((state) => ({
      loadInitialData: state.loadInitialData,
    })),
  );

  const { handleDeliveryOptionOrFinishChanged, setViewingSizeKey } =
    usePrintsUiStore(
      useShallow((state) => ({
        handleDeliveryOptionOrFinishChanged:
          state.handleDeliveryOptionOrFinishChanged,
        setViewingSizeKey: state.setViewingSizeKey,
      })),
    );

  // --- HOOKS ---

  // previous mobile builder filtered sizes+photos to only those having photos - this is the equivalent to `sizes`
  const sizesHavingPhotos = useSizesWithPhotos(false).filter(
    SizeHelpers.sizeHasPrints,
  );
  // previous mobile builder also filtered the current finish sizes to those having photos, most of the time these will both be the same
  const sizesByFinish = useSizesWithPhotos(true);
  const sizesByFinishHavingPhotos = sizesByFinish.filter(
    SizeHelpers.sizeHasPrints,
  );

  const addPhotos = useAddPhotos();
  const deliveryOption = useSelectedDeliveryOption();
  const finish = useSelectedFinish();
  const availableSizes = useAvailableSizesForSelectedDeliveryAndFinish();
  const scrollRef = useCollapseOnScroll(); // Collapse summary chart on scroll of where `scrollRef` is attached
  const { hasAlbumImportPending } = useAlbumLoader({
    ready,
    uploadFromGalleryListenerUrl,
    uploadFromAlbumUrl,
  }); // Hook to load photos from a gallery URL or localStorage

  useViewportResize(); // Handle browser resize event
  useBeforeUnloadWarning(); // Handle showing a warning to the user if they try to navigate away
  useGa4EventSubscriber(); // Listen for GA4 events
  useFocusStyles(); // Apply focus-related accessibility styles on the document body

  // --- SELECTORS ---
  const hasPhotos = sizesByFinishHavingPhotos.some(
    (size) => size.photos.length > 0,
  ); // check filtered sizes for photos so that its specific to current view
  const invalidDeliveryOptionSelected = useInvalidDeliveryOptionSelected();
  const { photosWithDuplicates = [], totalDuplicatesCount = 0 } =
    duplicatePhotoDetectionEnabled
      ? listDuplicatePhotos(sizesByFinishHavingPhotos)
      : {};
  const editingDisabled = !!currentlyLoading || isSaving;
  useAutoOpenPhotoLoader({
    hasPhotos,
    uploadFromGalleryListenerUrl,
    addPhotos,
    hasAlbumImportPending,
  });

  // --- EFFECTS ---

  // Trigger data load on component mount
  useEffectOnce(
    ([urlsDep, featuresDep, initSessionTimeoutDep, loadInitialDataDep]) => {
      loadInitialDataDep({
        urls: urlsDep,
        features: featuresDep,
      });
      if (initSessionTimeoutDep) {
        startSessionTimeout();
      }
    },
    [urls, features, initSessionTimeout, loadInitialData],
    () => true,
  );

  // --- RENDER ---

  const handleBulkPhotoResize = useCallback(
    (newSizeKey) => {
      bulkPhotoResize(newSizeKey);
      setViewingSizeKey(newSizeKey);
    },
    [bulkPhotoResize, setViewingSizeKey],
  );

  // chore: previous builder used laborious method of listing photos
  // TODO: consolidate photo display on desktop and mobile and move to single hook
  const photoIds = useMemo(() => {
    const byRoot = allPhotosCollectedByRootImage(sizesHavingPhotos);
    return Object.keys(byRoot).reduce((acc, key) => {
      acc[key] = Object.values(byRoot[key] || {})
        .flat()
        .map((photo) => photo.id);
      return acc;
    }, {});
  }, [sizesHavingPhotos]);
  const orderedPhotos = useMemo(
    () =>
      sortedAndFilteredImages({
        sizes: sizesByFinishHavingPhotos, // prefer pre-filtered sizes vs legacy builder
        photoIds,
        viewingSizeKey,
        finish,
      }),
    [sizesByFinishHavingPhotos, photoIds, viewingSizeKey, finish],
  );
  const orderedPhotosLength = useMemo(
    () => totalQuantity(orderedPhotos),
    [orderedPhotos],
  );

  return (
    <div className="prints-builder__grid m-web">
      <UploadManager />
      <LegacyEventHandler />

      <header className="prints-builder__header d-flex align-items-center">
        <div className="flex-grow-1">
          <SiteLogo />
        </div>
        <Totaliser sizes={sizesHavingPhotos} deliveryOption={deliveryOption} />
      </header>
      <aside className="print-order__summary-and-filter">
        {features.allowMultiplePrintFinishesPerOrder && (
          <PrintFinishTabs finishes={allFinishes} selectedFinish={finish} />
        )}
        <div className="grey-zone print-order__summary-container">
          <div className="d-flex align-items-center">
            <div className="flex-grow-1">
              <button
                className="btn btn-link p-0 border-0 collapsed print-order__title"
                type="button"
                data-toggle="collapse"
                data-target="#summary-chart"
                aria-expanded="true"
                aria-controls="summary-chart"
                aria-label="Your Print Order">
                <span>Print order</span>
              </button>
            </div>
            <div>
              <AddToCartControls
                enabled={hasPhotos && !currentlyLoading}
                basketUrl={urls.cart_page}
                onAddToCart={handleAddToCart}
              />
            </div>
          </div>

          <div id="summary-chart" className="collapse show">
            <hr />
            {debugEnabled && <DebugControls />}
            <div className="print-order__summary">
              {!features.allowMultiplePrintFinishesPerOrder && (
                <FinishSelector
                  deliveryOption={deliveryOption || {}}
                  finishesByDeliveryOption={finishesByDeliveryOption}
                  finishes={allFinishes}
                  finish={finish || {}}
                  onChange={handleDeliveryOptionOrFinishChanged}
                  newPrintsBuilderDeliveryAndFinishIcons={
                    features.newPrintsBuilderDeliveryAndFinishIcons
                  }
                />
              )}
              <DeliverySelector
                deliveryOptions={allDeliveryOptions}
                deliveryOption={deliveryOption || {}}
                finishesByDeliveryOption={finishesByDeliveryOption}
                finish={finish || {}}
                onChange={handleDeliveryOptionOrFinishChanged}
                newPrintsBuilderDeliveryAndFinishIcons={
                  features.newPrintsBuilderDeliveryAndFinishIcons
                }
              />
              <BulkQuantityChange
                availableSizes={sizesByFinish} // this needs photos for each size
                photos={photos} // only used for rootIds
                deliveryOption={deliveryOption || {}}
                disabled={!hasPhotos || !!currentlyLoading}
              />
            </div>
          </div>
        </div>
        <UploadProgressBar {...currentlyLoading} />
        {hasPhotos && (
          <div className="print-order__filter">
            <div
              className={`d-flex ${duplicatePhotoDetectionEnabled ? 'justify-content-between' : 'justify-content-center'}`}>
              {duplicatePhotoDetectionEnabled ? (
                <DuplicatePhotoViewToggle
                  totalDuplicatesCount={totalDuplicatesCount}
                  duplicatePhotoViewActive={
                    currentView === VIEW_MODE.DUPLICATES
                  }
                />
              ) : null}
              <PhotoRemoveAllButton
                disabled={!hasPhotos || editingDisabled}
                finish={finish}
                multipleFinishesPerOrder={
                  features.allowMultiplePrintFinishesPerOrder
                }
              />
              <PhotoUndoRemovalButton disabled={editingDisabled} />
            </div>
            <PhotosFilter
              sizes={sizesByFinishHavingPhotos}
              availableSizes={availableSizes}
              deliveryOption={deliveryOption}
              viewingSizeKey={viewingSizeKey}
              onChange={setViewingSizeKey}
              onResize={handleBulkPhotoResize}></PhotosFilter>
          </div>
        )}
      </aside>

      <main className="prints-builder__list" ref={scrollRef}>
        <MainView
          orderedPhotosLength={orderedPhotosLength}
          addPhotos={addPhotos}
          currentView={currentView}
          totalDuplicatesCount={totalDuplicatesCount}
          photosWithDuplicates={photosWithDuplicates}
          sizesByFinish={sizesByFinishHavingPhotos}
          editingDisabled={editingDisabled}
          availableSizes={availableSizes}
          deliveryOption={deliveryOption}
          viewingSizeKey={viewingSizeKey}
          orderedPhotos={orderedPhotos}
          invalidDeliveryOptionSelected={invalidDeliveryOptionSelected}
          currentlyLoading={currentlyLoading}
        />
      </main>
      <FullScreenSpinner show={!ready} />
      {!orderedPhotosLength && ready && (
        <Footer row={true}>
          <Button
            className="btn btn-link btn-underline"
            onClick={addPhotos}
            disabled={
              !ready ||
              editingDisabled ||
              invalidDeliveryOptionSelected ||
              currentView === VIEW_MODE.DUPLICATES
            }>
            + Add more photos
          </Button>
          {hasRemovedPhotos && (
            <PhotoUndoRemovalButton disabled={editingDisabled} />
          )}
        </Footer>
      )}

      <PhotoRemoveConfirmation />
      <DuplicatesWarningModal />
      <InvalidSizesModal />
      <InvalidDeliveryOptionModal />
      <AdvancedEditorWrapper />
    </div>
  );
};

MobilePrintsBuilder.propTypes = {
  urls: PrintsBuilderUrlsShape.isRequired,
  features: FeaturesShape.isRequired,
  uploadFromGalleryListenerUrl: PropTypes.string,
  initSessionTimeout: PropTypes.bool,
};

export default MobilePrintsBuilder;
